// A06 + A10 — App Users list. Same TVP2 §9 styling as AdminUserList:
// bordered Search filter card → bordered action bar + table card,
// Bootstrap palette (Search/Submit blue · Add green · Reset/Delete grey).
//
// Columns mirror the reference (User Name | Role | E-mail | Phone | Manage)
// with checkbox selection. Per-row actions: edit, reset password, delete.
// Bulk Delete soft-disables via PATCH is_active=false (no hard delete) —
// matches admin-users semantics.

import { useState, type FormEvent } from "react";
import { useMutation } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  Tab,
  Tabs,
  TextField,
} from "@mui/material";
import {
  Edit as EditIcon,
  Search as SearchIcon,
  Delete as DeleteIcon,
  LockReset as ResetIcon,
} from "@mui/icons-material";
import { tokens } from "@findgeeks/ui-tokens";
import { apiFetch } from "@/lib/api";
import { DataTable, type DataTableColumn } from "@/components/DataTable";
import { ConfirmModal } from "@/components/ConfirmModal";
import { useToast } from "@/components/Toast";
import { usePagedQuery } from "@/hooks/usePagedQuery";

interface AppUserRow {
  id: string;
  display_name: string | null;
  email: string | null;
  phone: string | null;
  role: string;
  is_active: boolean;
}

export function AppUserList() {
  const navigate = useNavigate();
  const { show, ToastEl } = useToast();

  // Local search inputs vs applied filters (sent to API on Search click).
  const [nameQ, setNameQ] = useState("");
  const [emailQ, setEmailQ] = useState("");
  const [statusQ, setStatusQ] = useState<"" | "active" | "inactive">("");
  const [roleQ, setRoleQ] = useState<"" | "customer" | "engineer">("");
  const [filters, setFilters] = useState<{
    q?: string;
    role?: string;
    status?: string;
  }>({});

  const [selected, setSelected] = useState<string[]>([]);
  const [deleteOpen, setDeleteOpen] = useState(false);

  const list = usePagedQuery<AppUserRow>("app-users", {
    filters,
    initialPagination: { page: 0, pageSize: 10 },
  });

  const onSearch = (e: FormEvent) => {
    e.preventDefault();
    // q on the backend matches name OR email OR phone — see admin.ts
    // /app-users handler. We collapse the User Name + E-mail boxes into a
    // single q param when one or both are filled (matches AdminUserList's
    // single-q behaviour when the API doesn't support per-field filters).
    const q = [nameQ.trim(), emailQ.trim()].filter(Boolean).join(" ").trim();
    setFilters({
      q: q || undefined,
      role: roleQ || undefined,
      // Backend handler reads `status=active|inactive` via c.req.query
      // (see /app-users handler in admin.ts) — NOT `is_active=true/false`.
      status: statusQ || undefined,
    });
    list.setPaginationModel({ ...list.paginationModel, page: 0 });
  };
  const onReset = () => {
    setNameQ("");
    setEmailQ("");
    setRoleQ("");
    setStatusQ("");
    setFilters({});
    list.setPaginationModel({ ...list.paginationModel, page: 0 });
  };

  const deleteMutation = useMutation({
    mutationFn: async (ids: string[]) => {
      const results: Array<{ id: string; ok: boolean }> = [];
      for (let i = 0; i < ids.length; i++) {
        const id = ids[i]!;
        try {
          await apiFetch(`/v1/admin/app-users/${id}`, { method: "DELETE" });
          results.push({ id, ok: true });
        } catch {
          results.push({ id, ok: false });
        }
        if (i < ids.length - 1) await new Promise((r) => setTimeout(r, 200));
      }
      return results;
    },
    onSuccess: (results) => {
      const okCount = results.filter((r) => r.ok).length;
      const failCount = results.length - okCount;
      show(
        failCount === 0
          ? `Deleted ${okCount} of ${results.length}`
          : `Deleted ${okCount} of ${results.length}; ${failCount} failed`,
        failCount === 0 ? "success" : "warning",
      );
      setSelected([]);
      setDeleteOpen(false);
      list.invalidate();
    },
  });

  const columns: DataTableColumn<AppUserRow>[] = [
    {
      field: "display_name",
      headerName: "User Name",
      render: (r) => r.display_name ?? "-",
    },
    {
      field: "role",
      headerName: "Role",
      render: (r) =>
        r.role === "customer" ? "Customer" : r.role === "engineer" ? "Engineer" : r.role,
    },
    { field: "email", headerName: "E-mail", render: (r) => r.email ?? "-" },
    { field: "phone", headerName: "Phone", render: (r) => r.phone ?? "-" },
    {
      field: "is_active",
      headerName: "Status",
      render: (r) => (r.is_active ? "Active" : "Inactive"),
    },
  ];

  // Bootstrap palette — same as AdminUserList.
  const baseBtn = {
    px: 4,
    textTransform: "none" as const,
    fontWeight: tokens.font.weights.medium,
    color: "#fff",
    boxShadow: "none",
  };
  const searchBtn = {
    ...baseBtn,
    backgroundColor: "#007bff",
    "&:hover": { backgroundColor: "#0069d9", boxShadow: "none" },
  };
  const addBtn = {
    ...baseBtn,
    backgroundColor: "#5CB85C",
    "&:hover": { backgroundColor: "#4CAE4C", boxShadow: "none" },
  };
  const greyBtn = {
    ...baseBtn,
    backgroundColor: "#D8D8D8",
    "&:hover": { backgroundColor: "#C8C8C8", boxShadow: "none" },
    "&.Mui-disabled": {
      backgroundColor: "#E8E8E8",
      color: "rgba(255,255,255,0.85)",
    },
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Search filter card */}
      <Paper
        elevation={0}
        sx={{
          mb: 3,
          border: `1px solid ${tokens.colors.admin.border}`,
          borderRadius: 1,
        }}
      >
        <Tabs
          value="search"
          sx={{
            minHeight: 38,
            borderBottom: `1px solid ${tokens.colors.admin.border}`,
            "& .MuiTabs-indicator": {
              backgroundColor: tokens.colors.admin.tabActive,
              height: 2,
            },
            "& .MuiTab-root": {
              minHeight: 38,
              textTransform: "none",
              fontSize: 13,
              color: tokens.colors.admin.textHeading,
              "& .MuiTab-iconWrapper": {
                marginRight: 0.5,
                marginBottom: "0 !important",
                "& svg": { fontSize: 16 },
              },
            },
          }}
        >
          <Tab
            value="search"
            iconPosition="start"
            icon={<SearchIcon />}
            label="Search"
          />
        </Tabs>

        <Box component="form" onSubmit={onSearch} sx={{ p: 3 }}>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "1fr 1fr 1fr 1fr" },
              gap: 3,
              mb: 3,
            }}
          >
            <TextField
              label="User Name"
              variant="standard"
              fullWidth
              value={nameQ}
              onChange={(e) => setNameQ(e.target.value)}
            />
            <TextField
              label="E-mail"
              variant="standard"
              fullWidth
              value={emailQ}
              onChange={(e) => setEmailQ(e.target.value)}
            />
            <TextField
              label="Status"
              variant="standard"
              select
              fullWidth
              value={statusQ}
              onChange={(e) => setStatusQ(e.target.value as typeof statusQ)}
            >
              <MenuItem value="active">Active</MenuItem>
              <MenuItem value="inactive">Inactive</MenuItem>
            </TextField>
            <TextField
              label="Role"
              variant="standard"
              select
              fullWidth
              value={roleQ}
              onChange={(e) => setRoleQ(e.target.value as typeof roleQ)}
            >
              <MenuItem value="customer">Customer</MenuItem>
              <MenuItem value="engineer">Engineer</MenuItem>
            </TextField>
          </Box>
          <Stack direction="row" spacing={2}>
            <Button type="submit" variant="contained" sx={searchBtn}>
              Search
            </Button>
            <Button type="button" variant="contained" onClick={onReset} sx={greyBtn}>
              Reset
            </Button>
          </Stack>
        </Box>
      </Paper>

      {/* Action bar + table card */}
      <Paper
        elevation={0}
        sx={{
          border: `1px solid ${tokens.colors.admin.border}`,
          borderRadius: 1,
          overflow: "hidden",
        }}
      >
        <Stack direction="row" spacing={2} sx={{ px: 3, pt: 3, pb: 2 }}>
          <Button
            variant="contained"
            sx={addBtn}
            onClick={() => navigate("/app/app-users/new")}
          >
            Add
          </Button>
          <Button
            variant="contained"
            sx={greyBtn}
            disabled={selected.length === 0 || deleteMutation.isPending}
            onClick={() => setDeleteOpen(true)}
          >
            Delete
          </Button>
        </Stack>

        <Box sx={{ px: 3, pb: 2 }}>
          <DataTable
            columns={columns}
            rows={list.data?.items ?? []}
            rowCount={list.data?.total ?? 0}
            paginationModel={list.paginationModel}
            onPaginationModelChange={list.setPaginationModel}
            getRowId={(r) => r.id}
            checkboxSelection
            selectedIds={selected}
            onSelectionChange={setSelected}
            loading={list.isLoading}
            bordered={false}
            rowActions={(row) => (
              <Stack direction="row" spacing={0}>
                <IconButton
                  size="small"
                  aria-label="Edit"
                  onClick={() => navigate(`/app/app-users/${row.id}`)}
                  sx={{ color: tokens.colors.admin.textMuted }}
                >
                  <EditIcon fontSize="small" />
                </IconButton>
                <IconButton
                  size="small"
                  aria-label="Reset password"
                  onClick={() => navigate(`/app/app-users/${row.id}/reset`)}
                  sx={{ color: tokens.colors.admin.textMuted }}
                >
                  <ResetIcon fontSize="small" />
                </IconButton>
                <IconButton
                  size="small"
                  aria-label="Delete"
                  onClick={() => {
                    setSelected([row.id]);
                    setDeleteOpen(true);
                  }}
                  sx={{ color: tokens.colors.admin.textMuted }}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Stack>
            )}
          />
        </Box>
      </Paper>

      <ConfirmModal
        open={deleteOpen}
        title="Delete app users"
        body={`About to delete ${selected.length} app user${selected.length === 1 ? "" : "s"}. This soft-disables the account(s). Continue?`}
        confirmLabel="Delete"
        danger
        pending={deleteMutation.isPending}
        onConfirm={() => deleteMutation.mutate(selected)}
        onCancel={() => setDeleteOpen(false)}
      />
      {ToastEl}
    </Box>
  );
}
